import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import Consultation from "@/components/sections/Consultation";
import SEOHead from "@/components/SEOHead";
import { ShieldCheck, CheckCircle2, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  RelatedPages,
  allTreatmentsLinks,
  allServiceLinks,
  allDecisionLinks,
} from "@/components/RelatedPages";
export const CostInsurancePage = () => {
  const networkInsurances = [
    "AARP",
    "Aetna",
    "Anthem Blue Cross Blue Shield",
    "Care Plus",
    "Cigna",
    "ComPsych",
    "First Health",
    "Florida Blue / BCBS",
    "Humana",
    "Magellan Health",
    "Medicare",
    "Optum Health",
    "Tricare",
    "UMR",
    "United Healthcare",
    "Veterans Affairs (VA)",
  ];
  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={"Cost & Insurance | Neuro Wellness TMS"}
        description={
          "Detailed information on insurance coverage, Medicare, Tricare, VA benefits, and CareCredit financing for TMS, SAINT® TMS, SPRAVATO®, and psychiatric services."
        }
        canonical={"/cost-insurance"}
        keywords={
          "cost and insurance TMS Coral Springs, Medicare covered TMS, CareCredit financing mental health, SPRAVATO insurance coverage Florida"
        }
      />

      <Navbar />
      <main className="pt-16 sm:pt-20">
        <section className="relative bg-gradient-navy text-primary-foreground py-16 sm:py-24 overflow-hidden">
          <div className="container-px relative mx-auto max-w-5xl text-center space-y-4">
            <span className="inline-block rounded-md bg-accent px-6 py-2 text-sm sm:text-base font-bold text-white shadow-md uppercase tracking-wider">
              Financial Transparency
            </span>
            <h1 className="font-headings text-3xl sm:text-5xl font-extrabold text-white">
              Cost & Insurance Coverage
            </h1>
            <p className="text-base sm:text-lg text-primary-foreground/80 max-w-3xl mx-auto">
              We make advanced psychiatric care accessible. Learn about covered
              insurance plans, prior authorization support, and flexible
              financing options.
            </p>
          </div>
        </section>

        <section className="py-12 sm:py-16 bg-background">
          <div className="container-px mx-auto max-w-5xl space-y-10">
            <div className="rounded-2xl bg-card border border-border p-6 sm:p-10 shadow-soft space-y-6">
              <div className="flex items-center gap-3 text-primary">
                <ShieldCheck className="h-8 w-8 text-accent shrink-0" />
                <h2 className="font-headings text-2xl font-bold">
                  In-Network Insurance & Benefit Verification
                </h2>
              </div>
              <p className="text-foreground/85 text-base sm:text-lg leading-relaxed">
                TMS Therapy and SPRAVATO® esketamine are covered by Medicare,
                Tricare, Veterans Affairs (VA), and most commercial insurers
                including BCBS, Aetna, Cigna, and United Healthcare. Coverage
                for accelerated TMS protocols like SAINT varies by plan and is
                reviewed case by case.
              </p>
              <p className="text-foreground/85 text-base sm:text-lg leading-relaxed">
                Verifying benefits and obtaining authorization are different
                processes. Our financial coordinators handle both — we submit
                the prior authorization request and manage appeals on your
                behalf, and we confirm your exact out-of-pocket cost before
                treatment begins.
              </p>
              <p className="text-foreground/85 text-base sm:text-lg leading-relaxed">
                Medicaid and Medicare Advantage plans generally do not cover
                SAINT at this time. Some plans, including Blue Cross Blue
                Shield, AmeriHealth, and Independence Blue Cross, have options
                within their policies for accelerated TMS protocols.
              </p>
              <div className="pt-2">
                <Button
                  asChild
                  size="lg"
                  className="bg-gradient-accent text-accent-foreground shadow-glow hover:opacity-90"
                >
                  <a href="tel:9548279793">
                    <Phone className="h-4 w-4 mr-2" />
                    Verify Benefits: (954) 827-9793
                  </a>
                </Button>
              </div>
            </div>

            <div className="rounded-2xl bg-card border border-border p-6 sm:p-10 shadow-soft space-y-6">
              <h3 className="font-headings text-xl font-bold text-primary">
                Accepted Insurance Providers
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {networkInsurances.map((ins, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 bg-muted/50 p-3 rounded-lg border border-border text-xs font-semibold text-primary"
                  >
                    <CheckCircle2 className="h-4 w-4 text-accent shrink-0" />
                    <span>{ins}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <RelatedPages
          heading="Explore Related Resources"
          links={[
            ...allTreatmentsLinks,
            ...allServiceLinks,
            ...allDecisionLinks,
            {
              title: "Financing Options",
              href: "/contents/patient-info/financing-options",
              description: "Payment plans and financing",
            },
          ]}
        />
        <Consultation />
      </main>
      <Footer />
    </div>
  );
};
export default CostInsurancePage;
